import { useRef, useId } from 'react';
import { Bewerber } from '../../../types/bewerber';
import { useBewerberActions } from './useBewerberActions';
import { BewerberActionModals } from './BewerberActionModals';
import { ActionConfig } from './statusActions.config.js';
import { useDropdownContext } from '../../../context/DropdownContext';
import { PortalDropdownMenu } from '../../common/PortalDropdownMenu';

interface BewerberActionDropdownProps {
  bewerber: Bewerber;
  onActionComplete?: () => void;
  align?: 'left' | 'right';
  size?: 'sm' | 'md';
}

export function BewerberActionDropdown({
  bewerber,
  onActionComplete,
  align = 'right',
  size = 'md',
}: BewerberActionDropdownProps) {
  const dropdownId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isOpen, openDropdown, closeDropdown } = useDropdownContext();

  const {
    primaryActions,
    secondaryActions,
    dangerActions,
    isLoading,
    loadingAction,
    handleAction,
    modals,
    einladenModalProps,
  } = useBewerberActions({
    bewerber,
    onActionComplete: () => {
      closeDropdown();
      onActionComplete?.();
    },
  });

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isOpen(dropdownId)) {
      closeDropdown();
    } else {
      openDropdown(dropdownId);
    }
  };

  const handleActionClick = async (e: React.MouseEvent, action: ActionConfig) => {
    e.stopPropagation();
    e.preventDefault();

    // For navigation and modal actions, close dropdown immediately
    if (action.action === 'navigate' || action.action === 'modal') {
      closeDropdown();
    }

    await handleAction(action);
  };

  const buttonSizeClasses = size === 'sm'
    ? 'p-1'
    : 'p-1.5';

  const iconSizeClasses = size === 'sm'
    ? 'h-4 w-4'
    : 'h-5 w-5';

  const hasAnyActions = primaryActions.length > 0 || secondaryActions.length > 0 || dangerActions.length > 0;

  if (!hasAnyActions) {
    return null;
  }

  return (
    <>
      <div className="relative inline-block text-left">
        {/* Kebab menu trigger */}
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggle}
          disabled={isLoading}
          className={`
            inline-flex items-center justify-center rounded-md
            text-gray-500 hover:text-gray-700 hover:bg-gray-100
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
            ${buttonSizeClasses}
          `}
          title="Aktionen"
        >
          {isLoading ? (
            <svg className={`animate-spin ${iconSizeClasses}`} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className={iconSizeClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          )}
        </button>

        {/* Portal Dropdown menu */}
        <PortalDropdownMenu
          dropdownId={dropdownId}
          triggerRef={buttonRef}
          align={align}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            {/* Primary Actions */}
            {primaryActions.length > 0 && (
              <>
                {primaryActions.map((action) => (
                  <ActionMenuItem
                    key={action.key}
                    action={action}
                    isLoading={loadingAction === action.key}
                    isDisabled={isLoading}
                    onClick={handleActionClick}
                  />
                ))}
              </>
            )}

            {/* Divider before secondary actions */}
            {primaryActions.length > 0 && secondaryActions.length > 0 && (
              <div className="border-t border-gray-100 my-1" />
            )}

            {/* Secondary Actions */}
            {secondaryActions.map((action) => (
              <ActionMenuItem
                key={action.key}
                action={action}
                isLoading={loadingAction === action.key}
                isDisabled={isLoading}
                onClick={handleActionClick}
              />
            ))}

            {/* Divider before danger actions */}
            {(primaryActions.length > 0 || secondaryActions.length > 0) && dangerActions.length > 0 && (
              <div className="border-t border-gray-100 my-1" />
            )}

            {/* Danger Actions */}
            {dangerActions.map((action) => (
              <ActionMenuItem
                key={action.key}
                action={action}
                isLoading={loadingAction === action.key}
                isDisabled={isLoading}
                onClick={handleActionClick}
                isDanger
              />
            ))}
          </div>
        </PortalDropdownMenu>
      </div>

      {/* Modals */}
      <BewerberActionModals
        bewerber={bewerber}
        modals={modals}
        einladenModalProps={einladenModalProps}
        onModalComplete={() => {
          closeDropdown();
          onActionComplete?.();
        }}
      />
    </>
  );
}

interface ActionMenuItemProps {
  action: ActionConfig;
  isLoading: boolean;
  isDisabled: boolean;
  onClick: (e: React.MouseEvent, action: ActionConfig) => void;
  isDanger?: boolean;
}

function ActionMenuItem({ action, isLoading, isDisabled, onClick, isDanger }: ActionMenuItemProps) {
  const IconComponent = action.icon;

  return (
    <button
      type="button"
      onClick={(e) => onClick(e, action)}
      disabled={isDisabled || action.disabled}
      title={action.tooltip}
      className={`
        w-full flex items-center px-4 py-2 text-sm
        disabled:opacity-50 disabled:cursor-not-allowed
        ${isDanger
          ? 'text-red-700 hover:bg-red-50'
          : 'text-gray-700 hover:bg-gray-100'
        }
      `}
      role="menuitem"
    >
      <span className="mr-3 flex-shrink-0">
        {isLoading ? (
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <IconComponent />
        )}
      </span>
      <span className="truncate">
        {isLoading ? action.loadingLabel : action.label}
      </span>
    </button>
  );
}
